import { useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useNavigate } from 'react-router-dom';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { app, db } from '../firebase-config';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { StatusBar, Style } from '@capacitor/status-bar';

// Ensure this URL is correct
const API_URL = 'https://asia-south1-inewsbook-1402f.cloudfunctions.net/subscribeToTopic';

const NotificationHandler = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const subscribeTokenToTopic = async (token, topic) => {
    try {
      await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, topic }),
      });
      console.log('Subscribed to', topic);
    } catch (error) {
      console.error('Subscription failed', error);
    }
  };

  const saveTokenToFirestore = async (token) => {
    if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { fcmToken: token });
        console.log("Token saved to user profile");
    }
};

  useEffect(() => {

    const handleAdminSubscription = async () => {
        // Only run if we have a user, they are admin, and we have a token
        if (currentUser?.isAdmin) {
            // We need to get the token. On web it's different than native.
            let token = null;
            
            if (Capacitor.getPlatform() === 'web') {
                const messaging = getMessaging(app);
                token = await getToken(messaging, { vapidKey: 'BKYAMfeGPGpEQbNHXprrxxMw-1mFIPPmnRlY27SIv_6WUXvYRMasdI_BIFMo1rSQQ355ojj4Z5rROrJhhf0Dmi0' });
            } else {
                // For native, we usually store the token in Firestore or a ref
                // Let's pull it from the user's document where you saved it
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                token = userDoc.data()?.fcmToken;
            }

            if (token) {
                console.log("Re-verifying Admin Subscription for token:", token);
                await subscribeTokenToTopic(token, 'admin_alerts');
            }
        }
    };

    handleAdminSubscription();

    const setupStatusAndNavBars = async () => {
    if (Capacitor.getPlatform() === 'android') {
        try {
            // Set icons to Dark (for light backgrounds) or Light (for dark backgrounds)
            // This replaces the deprecated "setBackgroundColor" method
            await StatusBar.setStyle({ style: Style.Light }); 
        } catch (err) {
            console.error("System bar setup failed", err);
        }
    }
};
setupStatusAndNavBars();
    // 1. Setup Listeners
    const regListener = PushNotifications.addListener('registration', (token) => {
      console.log('Push Registration Success. Token:', token.value);
      saveTokenToFirestore(token.value);
      subscribeTokenToTopic(token.value, 'top_news');

      if (currentUser?.isAdmin) {
        console.log('Admin detected, subscribing to admin_alerts');
        subscribeTokenToTopic(token.value, 'admin_alerts');
      }
    });

    const regErrorListener = PushNotifications.addListener('registrationError', (error) => {
      console.error('Push Registration Error: ', error);
    });

    const msgListener = PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push Received:', notification);
      // toast(<div><b>{notification.title}</b><br/>{notification.body}</div>);
    });

    const actionListener = PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      const data = notification.notification.data || notification.data;
      console.log('Push Action Performed:', data);

      const action = data?.target_action;
      const newsId = data?.newsId;
      const url = data?.url;

      if (action === "open_comments" && newsId) {
        // Navigate to home but pass a query parameter to open the comment box
        navigate(`/?openComments=${newsId}`);
    } else if (action === "showTopNewsInternal" && newsId) {
        setTimeout(() => {
          navigate(`/news/${newsId}`);
        }, 100);
      } 
      else if (action === "showTopNewsExternal" && url) {
        import('@capacitor/browser').then(({ Browser }) => {
            Browser.open({ url: url });
        });
        navigate('/'); 
      } 
      else {
        navigate('/');
      }
    });

    // 2. Register for Push with Permission Logic
    const initPush = async () => {
        const platform = Capacitor.getPlatform();

        if (platform === 'android') {
            try {
                // A. Check current status
                let permStatus = await PushNotifications.checkPermissions();

                // B. Request permission if prompt is required
                if (permStatus.receive === 'prompt') {
                    permStatus = await PushNotifications.requestPermissions();
                }

                // C. If granted, proceed to register
                if (permStatus.receive === 'granted') {
                    // Create Channel (Must match backend ID exactly)
                    await PushNotifications.createChannel({
                        id: 'inews_top_stories_v4', // <--- KEEP THIS ID
                        name: 'Breaking News',
                        importance: 5,
                        // visibility: 1,
                        sound: 'default',
                        sound: 'notification_sound.mp3',
                        vibration: true,
                    });
                    // 💡 NEW: Dedicated Silent Channel for GNews (No sound, no vibration)
                    await PushNotifications.createChannel({
                      id: 'inews_silent_news_v1', // <-- New ID for GNews
                      name: 'Silent News & Updates',
                      importance: 2, // 2 = Low (No sound, no heads-up popup)
                      vibration: false,
                    });
                    // 2. NEW Interaction Channel with CUSTOM SOUND
                    await PushNotifications.createChannel({
                      id: 'inews_interactions_v2',
                      name: 'Likes & Comments',
                      importance: 5,
                      visibility: 1,
                      sound: 'notification_sound.mp3', // Extension needed here for Capacitor
                      vibration: true,
                    });
                    // 2. NEW: Admin Alerts Channel
                    await PushNotifications.createChannel({
                      id: 'inews_admin_alerts_v1', // <--- MATCH THIS WITH BACKEND
                      name: 'Admin Alerts (Reports/Money)',
                      importance: 5, // 5 = Urgent/Heads-up
                      visibility: 1,
                      sound: 'notification_sound.mp3', // Or a unique sound file
                      vibration: true,
                    });
                    
                    await PushNotifications.register();
                } else {
                    console.log('User denied push permissions');
                }
            } catch (err) {
                console.error("Android Push Registration failed", err);
            }
        } 
        else if (platform === 'web') {
            try {
                const messaging = getMessaging(app);
                const permission = await Notification.requestPermission();
                
                if (permission === 'granted') {
                    const token = await getToken(messaging, { 
                        vapidKey: 'BKYAMfeGPGpEQbNHXprrxxMw-1mFIPPmnRlY27SIv_6WUXvYRMasdI_BIFMo1rSQQ355ojj4Z5rROrJhhf0Dmi0' 
                    });

                    if (token) {
                        console.log('Web FCM Token:', token);
                        saveTokenToFirestore(token);
                        await subscribeTokenToTopic(token, 'top_news');

                        if (currentUser?.isAdmin) {
                          console.log('Web Admin detected, subscribing to admin_alerts');
                          await subscribeTokenToTopic(token, 'admin_alerts');
                        }
                    }
                }
                
                onMessage(messaging, (payload) => {
                  console.log('Message received. ', payload);
                    // toast(<div><b>{payload.notification.title}</b><br/>{payload.notification.body}</div>);
                });

            } catch (error) {
                console.error("Web Push Setup Error:", error);
            }
        }
    };
    
    initPush();

    return () => {
      regListener.remove();
      regErrorListener.remove(); // Don't forget to remove error listener
      msgListener.remove();
      actionListener.remove();
    };
  }, [navigate, currentUser]);

  return <ToastContainer position="top-right" />;
};

export default NotificationHandler;
        

// vapidKey: 'BKYAMfeGPGpEQbNHXprrxxMw-1mFIPPmnRlY27SIv_6WUXvYRMasdI_BIFMo1rSQQ355ojj4Z5rROrJhhf0Dmi0'